import { useEffect, useState } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import {
  Sparkles,
  FolderOpen,
  Wand2,
  ShieldCheck,
  ClipboardCheck,
  ArrowLeft,
  ArrowRight,
  X,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { Button } from './Button'

// ---------------------------------------------------------------------------
// Step content — the onboarding walkthrough copy, locked with the team.
// ---------------------------------------------------------------------------
interface WizardStep {
  icon: LucideIcon
  title: string
  body: string
}

const STEPS: WizardStep[] = [
  {
    icon: Sparkles,
    title: 'Welcome to your Zota application',
    body: "You're about to complete your Zota application. Here's how it works and what to expect.",
  },
  {
    icon: FolderOpen,
    title: 'Prepare your documents',
    body: 'Before you start, get your documents ready. With them on hand, the whole application takes less than 5 minutes.',
  },
  {
    icon: Wand2,
    title: 'AI fills the form, you stay in control',
    body: 'Upload your documents and AI will pre-fill the form for you. Review and edit anything that’s not right. Your progress is saved automatically, so you can come back to it anytime.',
  },
  {
    icon: ShieldCheck,
    title: 'Your files and data are secure',
    body: 'Your files and data are fully encrypted, never used to train external models, and never shared beyond your application.',
  },
  {
    icon: ClipboardCheck,
    title: 'Review your whole application',
    body: 'Before submitting, you’ll see your entire application end-to-end to review and make final changes. After submitting, your application is saved, so you can come back to it later.',
  },
]

interface WelcomeWizardProps {
  open: boolean
  // permanent=true means the applicant hit Skip → don't auto-show again.
  // permanent=false is a "for now" dismiss (Get started / Esc / click-outside).
  onClose: (permanent: boolean) => void
}

export function WelcomeWizard({ open, onClose }: WelcomeWizardProps) {
  const shouldReduceMotion = useReducedMotion()
  // [stepIndex, slideDirection] — direction drives the enter/exit slide.
  const [[step, direction], setStep] = useState<[number, number]>([0, 0])

  const isFirst = step === 0
  const isLast = step === STEPS.length - 1

  const paginate = (newDirection: number) =>
    setStep(([current]) => [current + newDirection, newDirection])

  // Reset to the first step whenever the wizard (re)opens. Done during render
  // (not in an effect) per React's "adjusting state when a prop changes".
  const [prevOpen, setPrevOpen] = useState(open)
  if (open !== prevOpen) {
    setPrevOpen(open)
    if (open) setStep([0, 0])
  }

  // Lock background scroll while the modal is open.
  useEffect(() => {
    if (!open) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [open])

  // Escape closes (counts as "skip").
  useEffect(() => {
    if (!open) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose(false)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open, onClose])

  const dur = shouldReduceMotion ? 0 : 0.3

  const slideVariants = {
    enter: (dir: number) => ({
      opacity: 0,
      x: shouldReduceMotion ? 0 : dir > 0 ? 28 : -28,
    }),
    center: { opacity: 1, x: 0 },
    exit: (dir: number) => ({
      opacity: 0,
      x: shouldReduceMotion ? 0 : dir > 0 ? -28 : 28,
    }),
  }

  const current = STEPS[step]
  const Icon = current.icon

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          role="presentation"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: dur }}
          onClick={() => onClose(false)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 60, // --z-overlay
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 'var(--spacing-6)',
            background: 'var(--color-scrim-dialog)',
            backdropFilter: 'var(--glass-subtle-blur)',
            WebkitBackdropFilter: 'var(--glass-subtle-blur)',
          }}
        >
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="welcome-wizard-title"
            initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 12, scale: shouldReduceMotion ? 1 : 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: shouldReduceMotion ? 0 : 8, scale: shouldReduceMotion ? 1 : 0.98 }}
            transition={{ duration: dur, ease: [0.0, 0.0, 0.2, 1.0] }}
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'relative',
              width: '100%',
              maxWidth: 460,
              background: 'var(--color-surface-container-lowest)',
              borderRadius: 'var(--radius-xl)',
              boxShadow: 'var(--shadow-ambient)',
              padding: 'var(--spacing-8)',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Skip / close */}
            <Button
              variant="quiet"
              onClick={() => onClose(true)}
              aria-label="Don't show the walkthrough again"
              style={{
                position: 'absolute',
                top: '32px',
                right: '16px',
                gap: 6,
              }}
            >
              Don't show again
              <X size={20} aria-hidden="true" />
            </Button>

            {/* Animated step content */}
            <div style={{ minHeight: 232, display: 'flex', flexDirection: 'column' }}>
              <AnimatePresence mode="wait" custom={direction}>
                <motion.div
                  key={step}
                  custom={direction}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: dur, ease: [0.0, 0.0, 0.2, 1.0] }}
                  style={{ display: 'flex', flexDirection: 'column' }}
                >
                  {/* Icon badge */}
                  <div
                    aria-hidden="true"
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: 'var(--radius-lg)',
                      background: 'var(--color-info-surface)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: 'var(--spacing-6)',
                    }}
                  >
                    <Icon size={28} color="var(--color-action-primary)" />
                  </div>

                  <h2
                    id="welcome-wizard-title"
                    style={{
                      fontFamily: 'var(--font-display)',
                      fontWeight: 'var(--weight-semibold)',
                      fontSize: 'var(--text-title-lg)',
                      color: 'var(--color-text-primary)',
                      lineHeight: 'var(--leading-tight)',
                      margin: '0 0 var(--spacing-3) 0',
                    }}
                  >
                    {current.title}
                  </h2>
                  <p
                    style={{
                      fontFamily: 'var(--font-body)',
                      fontSize: 'var(--text-body-md)',
                      color: 'var(--color-text-secondary)',
                      lineHeight: 'var(--leading-normal)',
                      margin: 0,
                    }}
                  >
                    {current.body}
                  </p>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Progress dots */}
            <div
              role="tablist"
              aria-label="Walkthrough progress"
              style={{
                display: 'flex',
                gap: 6,
                margin: 'var(--spacing-8) 0 var(--spacing-6)',
              }}
            >
              {STEPS.map((_, i) => (
                <span
                  key={i}
                  aria-hidden="true"
                  style={{
                    height: 6,
                    width: i === step ? 22 : 6,
                    borderRadius: 'var(--radius-full)',
                    background:
                      i === step
                        ? 'var(--color-action-primary)'
                        : 'var(--color-action-primary-focus)',
                    transition: 'width 250ms ease, background-color 250ms ease',
                  }}
                />
              ))}
            </div>

            {/* Footer controls */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 'var(--spacing-4)',
              }}
            >
              {!isFirst ? (
                <Button
                  variant="tertiary"
                  onClick={() => paginate(-1)}
                  style={{
                    height: '50px',
                    padding: 0,
                    minWidth: 'unset',
                    justifyContent: 'flex-start',
                    fontSize: 'var(--text-body-md)',
                  }}
                >
                  <ArrowLeft size={16} aria-hidden="true" />
                  Back
                </Button>
              ) : (
                <span />
              )}

              {isLast ? (
                <Button variant="primary" onClick={() => onClose(false)}>
                  Get started
                </Button>
              ) : (
                <Button variant="primary" onClick={() => paginate(1)}>
                  Next
                  <ArrowRight size={16} aria-hidden="true" />
                </Button>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
